import React, { useState, useEffect } from 'react';
import { 
  Shield, Plus, Edit2, Trash2, Save, X, Users, Check, AlertCircle,
  ChevronDown, ChevronUp, Settings, Lock
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import BackButton from '../components/BackButton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_PERMISSIONS = [
  { key: 'all', label: 'Full Access', description: 'Complete system access' },
  { key: 'manage_products', label: 'Manage Products', description: 'Add, edit, delete products' },
  { key: 'manage_orders', label: 'Manage Orders', description: 'View and update orders' },
  { key: 'view_reports', label: 'View Reports', description: 'Access sales and analytics' },
  { key: 'pos', label: 'POS Access', description: 'Use point of sale system' },
  { key: 'picking', label: 'Picking Portal', description: 'Access order picking' },
  { key: 'deliveries', label: 'Deliveries', description: 'Manage deliveries' },
  { key: 'shop', label: 'Shop', description: 'Browse and purchase products' },
  { key: 'view_orders', label: 'View Orders', description: 'View own orders' },
  { key: 'manage_users', label: 'Manage Users', description: 'View and edit users' },
];

const COLOR_OPTIONS = [
  '#DC2626', '#EA580C', '#D97706', '#CA8A04', '#65A30D',
  '#16A34A', '#059669', '#0D9488', '#0891B2', '#0284C7',
  '#2563EB', '#4F46E5', '#7C3AED', '#9333EA', '#C026D3',
  '#DB2777', '#E11D48', '#6B7280', '#374151', '#1F2937'
];

const RoleManagement = ({ token: propToken }) => {
  const token = propToken || localStorage.getItem('auth_token');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
    color: '#2563EB'
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/superadmin/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
      }
    } catch (error) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!formData.name || !formData.display_name) {
      toast.error('Name and display name are required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/superadmin/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Role created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchRoles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create role');
      }
    } catch (error) {
      toast.error('Failed to create role');
    }
  };

  const handleUpdateRole = async () => {
    try {
      const response = await fetch(`${API_URL}/api/superadmin/roles/${editingRole.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          display_name: formData.display_name,
          description: formData.description,
          permissions: formData.permissions,
          color: formData.color
        })
      });

      if (response.ok) {
        toast.success('Role updated successfully');
        setEditingRole(null);
        resetForm();
        fetchRoles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteRole = async (roleName) => {
    if (!window.confirm(`Are you sure you want to delete the "${roleName}" role?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/superadmin/roles/${roleName}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Role deleted successfully');
        fetchRoles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete role');
      }
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      permissions: [],
      color: '#2563EB'
    });
  };

  const startEditing = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      display_name: role.display_name,
      description: role.description || '',
      permissions: role.permissions || [],
      color: role.color || '#2563EB'
    });
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="role-management-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <BackButton />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-7 h-7 text-purple-600" />
                Role Management
              </h1>
              <p className="text-gray-500 text-sm">Create and manage user roles and permissions</p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="create-role-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Role
          </Button>
        </div>

        {/* Roles Grid */}
        <div className="grid gap-4">
          {roles.map((role) => (
            <Card key={role.name} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: role.color || '#6B7280' }}
                  >
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{role.display_name}</h3>
                      {role.is_system && (
                        <Badge variant="outline" className="text-xs bg-gray-100">
                          <Lock className="w-3 h-3 mr-1" />
                          System
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{role.description || 'No description'}</p>
                    <p className="text-xs text-gray-400 mt-1">Role ID: {role.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedRole(expandedRole === role.name ? null : role.name)}
                  >
                    {expandedRole === role.name ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                  {!role.is_system || role.name !== 'superadmin' ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing(role)}
                        data-testid={`edit-role-${role.name}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {!role.is_system && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteRole(role.name)}
                          data-testid={`delete-role-${role.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  ) : null}
                </div>
              </div>

              {/* Expanded Permissions */}
              {expandedRole === role.name && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-2">Permissions:</p>
                  <div className="flex flex-wrap gap-2">
                    {(role.permissions || []).map((perm) => (
                      <Badge key={perm} variant="secondary" className="text-xs">
                        {AVAILABLE_PERMISSIONS.find(p => p.key === perm)?.label || perm}
                      </Badge>
                    ))}
                    {(!role.permissions || role.permissions.length === 0) && (
                      <span className="text-sm text-gray-400">No permissions assigned</span>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {(showCreateModal || editingRole) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold">
                    {editingRole ? 'Edit Role' : 'Create New Role'}
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingRole(null);
                      resetForm();
                    }}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {/* Role Name (only for create) */}
                  {!editingRole && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role ID (lowercase, no spaces)
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                        placeholder="e.g., warehouse_manager"
                        data-testid="role-name-input"
                      />
                    </div>
                  )}

                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <Input
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      placeholder="e.g., Warehouse Manager"
                      data-testid="role-display-name-input"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of this role"
                    />
                  </div>

                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role Color
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_OPTIONS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-8 h-8 rounded-lg transition-transform ${
                            formData.color === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Permissions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permissions
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                      {AVAILABLE_PERMISSIONS.map((perm) => (
                        <label
                          key={perm.key}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm.key)}
                            onChange={() => togglePermission(perm.key)}
                            className="w-4 h-4 text-purple-600 rounded"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{perm.label}</p>
                            <p className="text-xs text-gray-500">{perm.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingRole(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={editingRole ? handleUpdateRole : handleCreateRole}
                    data-testid="save-role-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingRole ? 'Save Changes' : 'Create Role'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleManagement;
